import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { Network, Bot, GitBranch, Sparkles } from "lucide-react";

const IMG = "https://images.pexels.com/photos/17483874/pexels-photo-17483874.png?auto=compress&cs=tinysrgb&w=1600";

const CARDS = [
  { icon: Network, title: "Neural platforms", desc: "Massive networks trained on enterprise data." },
  { icon: Bot, title: "Autonomous systems", desc: "Agents that operate, decide, and act." },
  {
    icon: GitBranch,
    title: "Knowledge graphs",
    desc: "Every entity connected, queryable, alive.",
    img: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/ecb94df848ce8799_GOVERNED%20STATE.png",
    imgAlt: "Governed state — every entity connected, queryable, alive",
  },
  {
    icon: Sparkles,
    title: "AI governance",
    desc: "Safe, auditable, board-ready deployment.",
    img: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/3c66cc72b3969ef9_The%20Governance%20Boundary_%20Bounded%20Execution%20Pathway%20%281%29.png",
    imgAlt: "The Governance Boundary — safe, auditable, board-ready deployment",
  },
];

export default function AICore() {
  return (
    <section
      id="ai"
      className="relative border-t border-white/5 bg-nexis-bg/85 py-28 backdrop-blur-sm md:py-40"
      data-testid="ai-section"
    >
      <div className="orb orb-purple left-[5%] top-[20%] h-[400px] w-[400px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— SCENE 05 / INTELLIGENCE</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight lg:text-6xl">
              An <span className="text-gradient">AI brain</span>, formed from light.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/60">
              Particles connect. Signals fire. We turn raw enterprise data into machine intelligence —
              from strategy through model toward responsibly governed autonomous systems, developed
              and deployed under human oversight.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="glass glass-hover flex h-full flex-col rounded-2xl p-6" data-testid={`ai-card-${i}`}>
                  <c.icon className="h-5 w-5 text-nexis-purple" />
                  <h3 className="mt-5 font-display text-base font-medium">{c.title}</h3>
                  <p className="mt-2 text-sm text-white/50">{c.desc}</p>
                  {c.img && (
                    <a
                      href={c.img}
                      target="_blank"
                      rel="noreferrer"
                      className="group/img mt-4 block overflow-hidden rounded-xl ring-1 ring-white/10"
                      data-testid={`ai-card-img-${i}`}
                    >
                      <img
                        src={c.img}
                        alt={c.imgAlt}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover object-top transition-transform duration-500 group-hover/img:scale-[1.04]"
                      />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <motion.div
            className="relative aspect-square overflow-hidden rounded-[2rem] glass"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 120, damping: 18 }}
          >
            <motion.img
              src={IMG}
              alt="Neural network"
              className="h-full w-full object-cover"
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-nexis-bg/80 via-transparent to-nexis-bg/30" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between font-mono text-xs text-white/60">
              <span>MODEL // NEXIS-CORE</span>
              <span className="text-nexis-blue">● ILLUSTRATIVE VISUAL</span>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
